import React, { useState, useEffect } from "react";
import {
  Heart,
  ShoppingCart,
  Share2,
  Trash2,
  Loader2,
  ChevronLeft,
} from "lucide-react";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";

const WishlistPage = () => {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");

  const navigate = useNavigate();
  const db = getFirestore();
  const auth = getAuth();
  const user = auth.currentUser;
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);
  useEffect(() => {
    if (user) {
      fetchWishlistItems();
    } else {
      setLoading(false);
      setError("Please sign in to view your wishlist");
    }
  }, [user]);

  const fetchWishlistItems = async () => {
    try {
      setLoading(true);
      const wishlistRef = collection(db, `users/${user.uid}/wishlist`);
      const q = query(wishlistRef);
      const querySnapshot = await getDocs(q);

      const items = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setWishlistItems(items);
    } catch (error) {
      console.error("Error fetching wishlist:", error);
      setError("Failed to load wishlist items");
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async (item) => {
    try {
      const cartRef = collection(db, `users/${user.uid}/cart`);
      await addDoc(cartRef, {
        productId: item.productId,
        name: item.name,
        price: item.price,
        images: [item.image],
        quantity: 1,
        addedAt: new Date().toISOString(),
      });

      await handleRemoveFromWishlist(item.id);

      // Show success feedback
      const notification = document.getElementById("notification");
      notification.style.display = "block";
      setTimeout(() => {
        notification.style.display = "none";
      }, 3000);
    } catch (error) {
      console.error("Error adding to cart:", error);
      alert("Failed to add item to cart");
    }
  };

  const handleRemoveFromWishlist = async (wishlistItemId) => {
    try {
      await deleteDoc(doc(db, "wishlist", wishlistItemId));
      setWishlistItems((prev) =>
        prev.filter((item) => item.id !== wishlistItemId)
      );
    } catch (error) {
      console.error("Error removing from wishlist:", error);
      alert("Failed to remove item from wishlist");
    }
  };

  const handleShareItem = (item) => {
    if (navigator.share) {
      navigator.share({
        title: item.name,
        text: `Check out this ${item.name} on Ambaari Jewels!`,
        url: window.location.href,
      });
    } else {
      alert("Sharing is not supported on this device");
    }
  };

  const filteredItems =
    selectedCategory === "all"
      ? wishlistItems
      : wishlistItems.filter((item) => item.category === selectedCategory);

  const MainContent = () => {
    if (loading) {
      return (
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <p className="text-red-500">{error}</p>
          {!user && (
            <button
              onClick={() => navigate("/login")}
              className="px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200"
            >
              Sign In
            </button>
          )}
        </div>
      );
    }

    if (wishlistItems.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 space-y-4 bg-white rounded-xl shadow-sm">
          <Heart className="w-16 h-16 text-purple-300" />
          <p className="text-purple-500 text-lg">Your wishlist is empty</p>
          <button
            onClick={() => navigate("/shop")}
            className="px-6 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 transform hover:scale-105 shadow-md"
          >
            Continue Shopping
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredItems.map((item) => (
          <div
            key={item.id}
            className="bg-white border border-purple-200 rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300"
          >
            <div className="relative">
              <img
                src={item.images[0]}
                alt={item.name}
                className="w-full h-48 object-cover"
              />
              <button
                onClick={() => handleRemoveFromWishlist(item.id)}
                className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-md text-red-500 hover:text-red-600 transition-colors duration-200"
              >
                <Trash2 className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              <h4 className="font-medium text-purple-900 text-lg">
                {item.name}
              </h4>
              <div className="flex justify-between items-center">
                <span className="text-xl font-medium text-purple-900">
                  ₹{item.price.toLocaleString()}
                </span>
                <span className="text-sm text-purple-500">
                  Added {new Date(item.addedAt).toLocaleDateString()}
                </span>
              </div>

              <div className="flex space-x-2 pt-3">
                <button
                  onClick={() => handleAddToCart(item)}
                  className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-purple-700 text-white rounded-lg hover:bg-purple-600 transition-all duration-200 transform hover:scale-105 shadow-md"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>Add to Cart</span>
                </button>
                <button
                  onClick={() => handleShareItem(item)}
                  className="p-2 border border-purple-200 rounded-lg text-purple-600 hover:bg-purple-50 transition-colors duration-200"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <Header />

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 mb-6">
          <button
            onClick={() => navigate(-1)}
            className="text-purple-600 hover:text-purple-800 flex items-center"
          >
            <ChevronLeft className="w-4 h-4" />
            <span>Back</span>
          </button>
          <span className="text-purple-300">/</span>
          <span className="text-purple-900">Wishlist</span>
        </div>

        {/* Page Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-medium text-purple-900">
              My Wishlist
            </h1>
            <p className="text-purple-600 mt-1">
              {wishlistItems.length} items saved
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-4 py-2 border border-purple-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="necklaces">Necklaces</option>
              <option value="rings">Rings</option>
              <option value="earrings">Earrings</option>
              <option value="bracelets">Bracelets</option>
            </select>
          </div>
        </div>

        {/* Main Content */}
        <MainContent />
      </div>

      {/* Success Notification */}
      <div
        id="notification"
        className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg hidden"
      >
        Item added to cart successfully!
      </div>

      <Footer />
    </div>
  );
};

export default WishlistPage;
